import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import * as z from "zod"
import { gql } from "~/__generated__"
import { GroupSort } from "~/__generated__/graphql"
import { loginPath, rootPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { FloatingTextField } from "~/fields/floating-text-field"
import { LoggedOutFooter } from "~/footers/logged-out-footer"
import { GroupIndexItem } from "~/groups/group-index-item"
import { PUBLIC_GROUPS_QUERY } from "~/groups/public-groups-screen"
import brandWhite from "~/images/brand-white"
import logoIcon from "~/images/logo-icon"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { Form } from "~/ui/form"
import { LoadingSpinner } from "~/ui/loading-spinner"
import { useToast } from "~/ui/use-toast"

const signupFormSchema = z.object({
  firstName: z.string().min(1, "First name cannot be empty"),
  lastName: z.string().min(1, "Last name cannot be empty"),
  email: z.string().email({ message: "Invalid email address" }),
})

type SignupFormValues = z.infer<typeof signupFormSchema>

const createUserMutation = gql(/* GraphQL */ `
  mutation UserCreate($input: UserCreateInput!) {
    userCreate(input: $input) {
      user {
        id
      }
    }
  }
`)

export const SignupScreen: React.FC = () => {
  const [createUser, { loading }] = useSafeMutation(createUserMutation)
  const {
    data: groupsData,
    loading: groupsLoading,
    error: groupsError,
  } = useQuery(PUBLIC_GROUPS_QUERY, {
    variables: {
      sort: GroupSort.RecentlyUsed,
    },
  })
  const { toast } = useToast()
  const navigate = useNavigate()

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  })

  const onSignupSubmit = async (values: SignupFormValues) => {
    const result = await createUser({
      variables: {
        input: {
          ...values,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    })

    if (result.errors) {
      console.error("Signup failed:", result.errors)
      toast({
        title: "Signup Failed",
        description: "An error occurred during signup. Please try again.",
        variant: "destructive",
      })
      return
    }

    if (result.data?.userCreate.user.id) {
      toast({
        title: "Account created",
        description: "Please sign in using your email to receive a login link.",
        variant: "default",
      })
      navigate(loginPath({}), { state: { email: values.email } })
    } else {
      toast({
        title: "Signup Failed",
        description: "Unable to create an account. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <div
      className="flex grid max-h-[700px] grid-cols-1 gap-8 bg-background pt-6 lg:grid-cols-3"
      data-testid="signup-screen"
    >
      {/* Signup Column */}
      <div className="col-span-1 w-full space-y-8">
        <div className="flex justify-center">
          <Link className="flex items-center" to={rootPath({})}>
            <img src={logoIcon.src} height={32} width={32} alt="Logo" />
            <span className="pl-2 text-xl font-bold">Tasti</span>
          </Link>
        </div>

        <h1 className="text-center text-2xl font-bold">Create Account</h1>
        <p className="text-center text-gray-600">
          Tasti is the smart way to save and share the best of the web with private and public
          groups… social bookmarking re-implemented with AI enhancements for 2025.
        </p>

        <div className="flex">
          <a
            href="https://chromewebstore.google.com/detail/tasti/fgfiahojpopkhmbnjgfenoeiefbdanoa"
            target="_blank"
            rel="noopener noreferrer"
            className="mx-auto flex h-[40px] items-center justify-center gap-2 rounded-md bg-brand px-4 text-white"
          >
            <img {...brandWhite} alt="" />
            Download the Extension
          </a>
        </div>

        <Form {...signupForm}>
          <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="flex flex-col">
            <div className="mb-8 flex flex-col gap-4">
              <FloatingTextField control={signupForm.control} name="firstName" label="First name" />
              <FloatingTextField control={signupForm.control} name="lastName" label="Last name" />
              <FloatingTextField control={signupForm.control} name="email" label="Email" />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Creating account …" : "Create account"}
            </Button>
          </form>
        </Form>

        <div className="space-y-2 text-center">
          <span className="text-muted-foreground">Already have an account? </span>
          <Link to={loginPath({})} className="hover:underline">
            Sign in
          </Link>
        </div>
        <LoggedOutFooter className="flex justify-center" />
      </div>

      {/* Public groups column  */}
      <div className="col-span-2 mb-4 w-full space-y-8 rounded-lg bg-gray-100 px-8 py-16">
        <h1 className="text-center text-2xl font-bold">Featured Groups</h1>
        <div>
          {groupsError ? (
            <GraphqlError error={groupsError} />
          ) : groupsLoading && !groupsData ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 [&>*]:h-full">
              {groupsData?.groups.edges
                .map((g) => g.node)
                .map((group) => <GroupIndexItem key={group.id} group={group} showFooter={false} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
